import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { ShieldCheck, ArrowLeft, Copy } from '@phosphor-icons/react'
import { RecoveryCodesPanel } from '@/components/security/RecoveryCodesPanel'

type Phase =
  | { kind: 'challenge'; challengeToken: string }
  | { kind: 'enroll'; setupToken: string; orgName?: string }

interface Props {
  phase: Phase
  onAuthenticated: () => void
  onBack: () => void
}

export function TwoFactorLoginStep({ phase, onAuthenticated, onBack }: Props) {
  if (phase.kind === 'challenge') {
    return <ChallengeStep challengeToken={phase.challengeToken} onAuthenticated={onAuthenticated} onBack={onBack} />
  }
  return (
    <ForcedEnrollStep
      setupToken={phase.setupToken}
      orgName={phase.orgName}
      onAuthenticated={onAuthenticated}
      onBack={onBack}
    />
  )
}

function ChallengeStep({
  challengeToken,
  onAuthenticated,
  onBack,
}: {
  challengeToken: string
  onAuthenticated: () => void
  onBack: () => void
}) {
  const { t } = useTranslation('auth')
  const { finishTwoFactor } = useAuth()
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const result = await api.verifyTwoFactor(challengeToken, code.trim())
      finishTwoFactor(result)
      onAuthenticated()
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      if (message.includes('LOCKED')) {
        toast.error(t('twoFactor.challenge.locked'))
        onBack()
      } else if (message.includes('INVALID')) {
        toast.error(t('twoFactor.challenge.invalid'))
      } else {
        toast.error(t('twoFactor.challenge.expired'))
        onBack()
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="text-center space-y-1">
        <ShieldCheck size={32} className="mx-auto text-[var(--pd-accent)]" />
        <h2 className="text-lg font-semibold">{t('twoFactor.challenge.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {useRecovery ? t('twoFactor.challenge.recoveryHint') : t('twoFactor.challenge.subtitle')}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="tfa-code">
          {useRecovery ? t('twoFactor.challenge.recoveryLabel') : t('twoFactor.challenge.codeLabel')}
        </Label>
        <Input
          id="tfa-code"
          autoFocus
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          maxLength={useRecovery ? 20 : 6}
          placeholder={useRecovery ? 'XXXXX-XXXXX' : '000000'}
          value={code}
          onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ''))}
          className="text-center tracking-[0.3em] text-lg"
        />
      </div>

      <Button type="submit" disabled={busy || code.length < (useRecovery ? 5 : 6)} className="w-full">
        {busy ? t('twoFactor.challenge.verifying') : t('twoFactor.challenge.verify')}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => {
            setUseRecovery((v) => !v)
            setCode('')
          }}
          className="text-[var(--pd-primary)] hover:underline"
        >
          {useRecovery ? t('twoFactor.challenge.useAuthenticator') : t('twoFactor.challenge.useRecovery')}
        </button>
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft size={14} /> {t('twoFactor.challenge.back')}
        </button>
      </div>
      <p className="text-center text-xs text-muted-foreground">{t('twoFactor.challenge.expiresHint')}</p>
    </form>
  )
}

function ForcedEnrollStep({
  setupToken,
  orgName,
  onAuthenticated,
  onBack,
}: {
  setupToken: string
  orgName?: string
  onAuthenticated: () => void
  onBack: () => void
}) {
  const { t } = useTranslation('auth')
  const { finishTwoFactor } = useAuth()
  const [qrCode, setQrCode] = useState('')
  const [manualKey, setManualKey] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  useEffect(() => {
    let active = true
    api
      .startTwoFactorSetup(setupToken)
      .then((res) => {
        if (!active) return
        setQrCode(res.qrCode)
        setManualKey(res.manualKey)
      })
      .catch(() => toast.error(t('twoFactor.errors.setupFailed')))
    return () => {
      active = false
    }
  }, [setupToken, t])

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const result = await api.confirmTwoFactorSetup(code, setupToken)
      setRecoveryCodes(result.recoveryCodes)
      if (result.status === 'authenticated' && result.user) {
        finishTwoFactor(result as Parameters<typeof finishTwoFactor>[0])
      }
    } catch {
      toast.error(t('twoFactor.challenge.invalid'))
    } finally {
      setBusy(false)
    }
  }

  if (recoveryCodes) {
    // Session is already established by confirm; show codes then continue.
    return <RecoveryCodesPanel codes={recoveryCodes} onDone={onAuthenticated} />
  }

  const copyManualKey = () => {
    void navigator.clipboard?.writeText(manualKey.replace(/\s/g, ''))
    toast.success(t('twoFactor.enroll.copied'))
  }

  return (
    <form onSubmit={confirm} className="space-y-4">
      <div className="text-center space-y-1">
        <ShieldCheck size={32} className="mx-auto text-[var(--pd-accent)]" />
        <h2 className="text-lg font-semibold">{t('twoFactor.enroll.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {orgName
            ? t('twoFactor.enroll.subtitleRequired', { org: orgName })
            : t('twoFactor.enroll.scanHint')}
        </p>
      </div>

      {qrCode && (
        <img
          src={qrCode}
          alt={t('twoFactor.enroll.qrAlt')}
          className="mx-auto h-44 w-44 rounded-lg border border-[var(--pd-border)] bg-white p-2"
        />
      )}

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{t('twoFactor.enroll.manualKey')}</Label>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded-md bg-[var(--pd-muted)] px-3 py-2 font-mono text-xs">
            {manualKey}
          </code>
          <Button type="button" variant="outline" size="sm" onClick={copyManualKey}>
            <Copy size={16} />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="enroll-code">{t('twoFactor.enroll.codeLabel')}</Label>
        <Input
          id="enroll-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="text-center tracking-[0.3em] text-lg"
        />
      </div>

      <Button type="submit" disabled={busy || code.length !== 6} className="w-full">
        {busy ? t('twoFactor.enroll.confirming') : t('twoFactor.enroll.confirm')}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="mx-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} /> {t('twoFactor.challenge.back')}
      </button>
    </form>
  )
}
